'use client';

import { useEffect, useState, type ReactNode } from 'react';

export interface FaqAccordionItem {
  /** Anchor id for the question (slugified). */
  id: string;
  question: string;
  /** Server-rendered answer — stays in the DOM when collapsed, so it's indexable. */
  answer: ReactNode;
}

/**
 * Collapsible Q&A list. Open/close animates purely in CSS
 * (`grid-template-rows: 0fr → 1fr`), so no measuring and no motion library.
 * The first question starts open; a URL hash matching a question opens that one.
 */
export default function FaqAccordion({ items }: { items: FaqAccordionItem[] }) {
  const [open, setOpen] = useState<Set<string>>(() => new Set(items[0] ? [items[0].id] : []));

  // Deep links (#question-slug) open their question — on load and on hash change.
  useEffect(() => {
    const openFromHash = () => {
      const hash = window.location.hash.slice(1);
      if (hash && items.some((it) => it.id === hash)) {
        setOpen((prev) => (prev.has(hash) ? prev : new Set(prev).add(hash)));
      }
    };
    const frame = window.requestAnimationFrame(openFromHash);
    window.addEventListener('hashchange', openFromHash);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('hashchange', openFromHash);
    };
  }, [items]);

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="la-faq-list">
      {items.map((it) => {
        const isOpen = open.has(it.id);
        return (
          <div key={it.id} id={it.id} className={`la-faq-item${isOpen ? ' open' : ''}`}>
            <h3 className="la-faq-q">
              <button
                type="button"
                id={`${it.id}-q`}
                aria-expanded={isOpen}
                aria-controls={`${it.id}-a`}
                onClick={() => toggle(it.id)}
              >
                <span>{it.question}</span>
                <span className="la-faq-icon" aria-hidden />
              </button>
            </h3>
            <div id={`${it.id}-a`} className="la-faq-a" role="region" aria-labelledby={`${it.id}-q`}>
              <div className="la-faq-a-inner">{it.answer}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
